import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Calendar } from "@/shared/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/shared/lib/utils";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { IUser } from "@/features/profiles/services/profiles/types";
import ProfilesService from "@/features/profiles/services/profiles/profileService";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { useTranslation } from "@/shared/i18n";

interface UserEditDialogProps {
  user: IUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserEditDialog({
  user,
  open,
  onOpenChange,
}: UserEditDialogProps) {
  const { t } = useTranslation('profiles');
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(user?.email || "");
  const [expiresAt, setExpiresAt] = useState<Date | null>(
    user?.subscription?.expiresAt ? new Date(user.subscription.expiresAt) : null
  );
  const [subscribed, setSubscribed] = useState(
    user?.subscription?.active || false
  );
  const [vip, setVip] = useState(user?.vip || false);
  const [vipExpiresAt, setVipExpiresAt] = useState<Date | null>(
    user?.vipExpiresAt ? new Date(user.vipExpiresAt) : null
  );

  const { mutate: updateUser } = useCustomMutation({
    serviceInstance: ProfilesService.Instance,
    selectAction: (s) => s.updateUser,
    onSuccess: () => {
      toast.success(t('userUpdated'));
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      toast.error(t('errorUpdatingUser'));
    },
  });

  const handleSave = async () => {
    if (!user) return;

    const newUser = {
      ...user,
      email,
      vip,
      vip_expires_at: vipExpiresAt.toISOString(),
      subscription: { active: subscribed, expiresAt: expiresAt.toISOString() },
    };

    updateUser(newUser);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-[#1A1A1A] border-[#333333] text-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            {user?.email || t('editUser')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white">
              {t('email')}
            </Label>
            <Input
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#222222] border-[#333333] text-white"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="vip" className="text-white">
                {t('vipLabel')}
              </Label>
              <div className="flex items-center gap-4">
                <Switch
                  id="vip"
                  checked={vip}
                  onCheckedChange={setVip}
                  className="data-[state=checked]:bg-[#C1174F]"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[180px] justify-start text-left font-normal bg-[#222222] border-[#333333] text-white",
                        !vipExpiresAt && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {vipExpiresAt
                        ? formatDate(vipExpiresAt)
                        : t('selectDate')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={vipExpiresAt}
                      onSelect={setVipExpiresAt}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="subscribed" className="text-white">
                {t('subscriber')}
              </Label>
              <div className="flex items-center gap-4">
                <Switch
                  id="subscribed"
                  checked={subscribed}
                  onCheckedChange={setSubscribed}
                  className="data-[state=checked]:bg-[#C1174F]"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[180px] justify-start text-left font-normal bg-[#222222] border-[#333333] text-white",
                        !expiresAt && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {expiresAt ? formatDate(expiresAt) : t('selectDate')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={expiresAt}
                      onSelect={setExpiresAt}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent hover:bg-[#333333] text-white border-[#333333]"
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="bg-[#C1174F] hover:bg-[#A01441] text-white"
          >
            {t('save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
